import type { ColumnDef } from '@tanstack/react-table';
import StatusBadge from '@/components/StatusBadge';
import { formatDate } from '@/lib/utils';
import MemberSelect from './MemberSelect';
import { Link } from 'react-router';

export type ProjectRow = {
  id: string;
  name: string;
  client_name: string;
  status: { label: string; colour: string };
  due_date: string;
  producers: { id: string; name: string }[];
  editors: { id: string; name: string }[];
  reviewers: { id: string; name: string }[];
};

export function getColumns(
  teamUsers: { id: string; name: string }[],
  onAssign: (projectId: string, userId: string, role: string) => void,
  onUnassign: (projectId: string, userId: string, role: string) => void
): ColumnDef<ProjectRow>[] {
  return [
    {
      accessorKey: 'status',
      header: 'Status',
      meta: { className: 'w-40' },
      cell: ({ row }) => (
        <StatusBadge
          label={row.original.status.label}
          colour={row.original.status.colour}
        />
      ),
    },
    {
      accessorKey: 'name',
      header: 'Project',
      cell: ({ row }) => (
        <Link to={`projects/${row.original.id}`}>{row.original.name}</Link>
      ),
    },
    {
      accessorKey: 'client_name',
      header: 'Client',
    },
    {
      accessorKey: 'due_date',
      header: 'Deliver by',
      cell: ({ row }) => formatDate(row.original.due_date),
    },
    {
      accessorKey: 'producers',
      header: 'Producers',
      cell: ({ row }) => {
        const assignedElsewhere = new Set([
          ...row.original.editors.map((u) => u.id),
          ...row.original.reviewers.map((u) => u.id),
        ]);
        const available = teamUsers.filter((u) => !assignedElsewhere.has(u.id));

        return (
          <MemberSelect
            projectId={row.original.id}
            role="producer"
            assigned={row.original.producers}
            teamUsers={available}
            onAssign={onAssign}
            onUnassign={onUnassign}
          />
        );
      },
    },
    {
      accessorKey: 'editors',
      header: 'Editors',
      cell: ({ row }) => {
        const assignedElsewhere = new Set([
          ...row.original.producers.map((u) => u.id),
          ...row.original.reviewers.map((u) => u.id),
        ]);
        const available = teamUsers.filter((u) => !assignedElsewhere.has(u.id));

        return (
          <MemberSelect
            projectId={row.original.id}
            role="editor"
            assigned={row.original.editors}
            teamUsers={available}
            onAssign={onAssign}
            onUnassign={onUnassign}
          />
        );
      },
    },
    {
      accessorKey: 'reviewers',
      header: 'Reviewers',
      cell: ({ row }) => {
        const assignedElsewhere = new Set([
          ...row.original.producers.map((u) => u.id),
          ...row.original.editors.map((u) => u.id),
        ]);
        const available = teamUsers.filter((u) => !assignedElsewhere.has(u.id));

        return (
          <MemberSelect
            projectId={row.original.id}
            role="reviewer"
            assigned={row.original.reviewers}
            teamUsers={available}
            onAssign={onAssign}
            onUnassign={onUnassign}
          />
        );
      },
    },
  ];
}
